import { useEffect, useState } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBars,
  faBuilding,
  faChevronDown,
  faChevronRight,
  faEnvelope,
  faLocationDot,
  faMagnifyingGlass,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import { getStoreContactEmail, getStoreContactPhone } from '../lib/dropify'
import type { StorefrontCategory, StorefrontConfig } from '../lib/dropify'
import { listStorefrontCategoriesServer } from '../lib/storefront'
import { whatsAppCtaProps, track, trackContact } from '../lib/analytics'
import { getCategoryNavigationPath } from '../lib/grosirLanding'
import { SOCIAL_LINKS } from '../data/social'
import { LoadingSpinner } from './LoadingState'

export const WA_NUMBER = '6285704011028'
export const FOOTER_BRAND_LINKS: Array<{ label: string; href: string }> = [
  { label: 'Masker ONEMED', href: '/masker-onemed' },
  { label: 'Masker COMET', href: '/masker-comet' },
  { label: 'Bra Oreo', href: '/bra-oreo' },
  { label: 'Sarung Tangan COMET', href: '/sarung-tangan-comet' },
  { label: 'Sarung Tangan Gekko', href: '/sarung-tangan-gekko' },
  { label: 'Sarung Tangan Matahari', href: '/sarung-tangan-matahari' },
  { label: 'Sarung Tangan Diamond', href: '/sarung-tangan-diamond' },
  { label: 'Sarung Tangan Bulldozer', href: '/sarung-tangan-bulldozer' },
  { label: 'Bra Guojisan', href: '/bra-guojisan' },
  { label: 'Bra Polini', href: '/bra-polini' },
  { label: 'Camisol Polini', href: '/camisol-polini' },
  { label: 'CD Haode', href: '/cd-haode' },
  { label: 'CD Hello', href: '/cd-hello' },
  { label: 'CD Lupo', href: '/cd-lupo' },
  { label: 'CD Oreo', href: '/cd-oreo' },
  { label: 'CD FEMA', href: '/cd-fema' },
  { label: 'CD Polini', href: '/cd-polini' },
  { label: 'Handuk Breitex', href: '/handuk-breitex' },
  { label: 'Kaos Kaki Gelea', href: '/kaos-kaki-gelea' },
  { label: 'Kaos Kaki Oreo', href: '/kaos-kaki-oreo' },
  { label: 'Kaos Kaki Tuyaya', href: '/kaos-kaki-tuyaya' },
  { label: 'Kaos Kaki Winteku', href: '/kaos-kaki-winteku' },
  { label: 'Manset Brilles', href: '/manset-brilles' },
  { label: 'Piyama Leisure', href: '/piyama-leisure' },
  { label: 'Sarung Tangan Lupo', href: '/sarung-tangan-lupo' },
  { label: 'Sarung Tangan Tuyaya', href: '/sarung-tangan-tuyaya' },
  { label: 'Lihat Semua Brand', href: '/merek' },
]

// ─── WhatsApp Icon ─────────────────────────────────────────────────────────────

export function WhatsAppIcon({ className }: { className?: string }) {
  return <FontAwesomeIcon icon={faWhatsapp} className={className ?? 'w-4 h-4'} />
}

// ─── Logo Lockup ──────────────────────────────────────────────────────────────

export function LogoLockup({ size = 'md', invert = false }: { size?: 'sm' | 'md'; invert?: boolean }) {
  const imgCls = size === 'sm' ? 'h-8 w-8 object-contain' : 'h-10 w-10 object-contain'
  const imgSize = size === 'sm' ? 32 : 40
  const logoSizes = `${imgSize}px`
  const textCls = size === 'sm'
    ? `text-base font-bold${invert ? ' text-white' : ' text-slate-900'}`
    : `text-xl font-bold${invert ? ' text-white' : ' text-slate-900'}`
  return (
    <div className="flex items-center gap-2.5">
      <img
        src="/logo40.webp"
        srcSet="/logo40.webp 40w, /logo80.webp 80w"
        sizes={logoSizes}
        alt="Dropify"
        width={imgSize}
        height={imgSize}
        className={imgCls}
      />
      <span className={textCls}>Dropify</span>
    </div>
  )
}

// ─── Sub-components ───────────────────────────────────────────────────────────

export function TopBar({ config }: { config?: StorefrontConfig | null } = {}) {
  const contactPhone = getStoreContactPhone(config)
  const contactEmail = getStoreContactEmail(config)

  return (
    <div className="hidden sm:block bg-slate-900 text-slate-300 text-xs py-2">
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="hidden sm:inline">Distributor grosir · Pemesanan melalui WhatsApp · Surabaya</span>
          <a href={`https://wa.me/${contactPhone.replace(/[^0-9]/g, '')}`} {...whatsAppCtaProps('sitewide', { cta_placement: 'topbar' })} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-blue-400 transition-colors">
            <WhatsAppIcon className="w-3.5 h-3.5 text-blue-400" />
            {contactPhone}
          </a>
          <a href={`mailto:${contactEmail}`} onClick={() => trackContact('email')} className="hidden md:flex items-center gap-1.5 hover:text-blue-400 transition-colors">
            <FontAwesomeIcon icon={faEnvelope} className="w-3.5 h-3.5 text-blue-400" />
            {contactEmail}
          </a>
        </div>
      </div>
    </div>
  )
}

export function HeaderMiddle() {
  return (
    <div className="bg-white shadow-sm py-3 border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 flex items-center gap-4 max-[360px]:flex-wrap">
        <a href="/" className="flex-shrink-0">
          <LogoLockup size="md" />
        </a>

        <form action="/search" method="get" className="flex flex-1 items-center min-w-0 max-w-2xl max-[360px]:w-full max-[360px]:max-w-none">
          <input
            type="text"
            name="q"
            placeholder="Cari produk..."
            className="flex-1 min-w-0 border border-slate-200 rounded-l-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/30 focus:border-blue-400 transition-colors"
          />
          <button type="submit" aria-label="Cari produk" className="flex-shrink-0 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-r-lg transition-colors flex items-center gap-1.5">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="w-4 h-4" />
            <span className="text-sm font-medium hidden sm:inline">Cari</span>
          </button>
        </form>
      </div>
    </div>
  )
}

export function Navbar({ categories: categoriesProp = [] }: { categories?: StorefrontCategory[] }) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [collectionOpen, setCollectionOpen] = useState(false)
  const [fetchedCategories, setFetchedCategories] = useState<StorefrontCategory[]>([])
  const [categoriesState, setCategoriesState] = useState<'loading' | 'ready' | 'error'>(
    categoriesProp.length > 0 ? 'ready' : 'loading',
  )

  // Only fetch when the caller didn't supply categories. Depend on the length
  // (a primitive), not the array identity: the default `categoriesProp = []` is a
  // new reference every render, which would otherwise re-run this effect each
  // render and, with the API cache off in dev, loop endlessly.
  useEffect(() => {
    if (categoriesProp.length > 0) {
      setCategoriesState('ready')
      return
    }
    let active = true
    setCategoriesState('loading')
    listStorefrontCategoriesServer()
      .then((items) => {
        if (!active) return
        setFetchedCategories(items)
        setCategoriesState('ready')
      })
      .catch(() => {
        if (active) setCategoriesState('error')
      })
    return () => { active = false }
  }, [categoriesProp.length])

  const categories = categoriesProp.length > 0 ? categoriesProp : fetchedCategories

  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const isActive = (href: string) => (href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(`${href}/`))
  const deskLink = (href: string) =>
    `px-4 py-3 block hover:text-blue-400 transition-colors${isActive(href) ? ' border-b-2 border-blue-500 text-blue-400' : ''}`

  return (
    <nav className="bg-slate-900 text-white relative z-50 shadow-md">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-12">
          {/* Desktop Nav */}
          <ul className="hidden md:flex items-center gap-0 text-sm font-medium">
            <li>
              <a href="/" className={deskLink('/')}>
                Beranda
              </a>
            </li>
            <li
              className="relative group"
              onMouseEnter={() => setCollectionOpen(true)}
              onMouseLeave={() => setCollectionOpen(false)}
            >
              <button className={`px-4 py-3 flex items-center gap-1 hover:text-blue-400 transition-colors${isActive('/kategori') ? ' border-b-2 border-blue-500 text-blue-400' : ''}`}>
                Koleksi <FontAwesomeIcon icon={faChevronDown} className="w-3.5 h-3.5" />
              </button>
              {collectionOpen && (
                <div className="absolute top-full left-0 bg-white text-slate-800 shadow-lg rounded-b-lg min-w-40 py-1 border-t-2 border-blue-500">
                  {categories.length > 0 ? categories.map((cat) => (
                    <a key={cat.id} href={`/kategori/${cat.slug}`} className="block px-4 py-2.5 text-sm hover:bg-blue-50 hover:text-blue-600 transition-colors">
                      {cat.name}
                    </a>
                  )) : categoriesState === 'loading' ? (
                    <div role="status" aria-live="polite" className="flex items-center gap-2 px-4 py-3 text-sm text-slate-600">
                      <LoadingSpinner className="h-4 w-4" /> Memuat kategori...
                    </div>
                  ) : (
                    <p className="px-4 py-3 text-sm text-slate-600">Kategori belum dapat ditampilkan.</p>
                  )}
                </div>
              )}
            </li>
            <li>
              <a href="/katalog" className={deskLink('/katalog')}>
                Katalog
              </a>
            </li>
            <li><a href="/merek" className={deskLink('/merek')}>Merek</a></li>
            <li><a href="/blog" className={deskLink('/blog')}>Cara Jualan</a></li>
            <li><a href="/cara-jadi-reseller" className={deskLink('/cara-jadi-reseller')}>Jadi Reseller</a></li>
            <li><a href="/ebook-bikin-toko-online" className={deskLink('/ebook-bikin-toko-online')}>Ebook</a></li>
            <li><a href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent('Halo Dropify, ada yang ingin saya tanyakan.')}`} {...whatsAppCtaProps('sitewide', { cta_placement: 'nav_desktop' })} target="_blank" rel="noopener noreferrer" className="px-4 py-3 block hover:text-blue-400 transition-colors">Kontak</a></li>
          </ul>

          {/* Mobile: hamburger only — logo lives in HeaderMiddle above */}
          <button
            className="md:hidden w-11 h-11 flex items-center justify-center hover:text-blue-400 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <FontAwesomeIcon icon={faXmark} className="w-5 h-5" /> : <FontAwesomeIcon icon={faBars} className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Menu — full-screen overlay */}
        {mobileOpen && (
          <div className="md:hidden fixed inset-0 z-[100] bg-slate-900/95 overflow-y-auto">
            <div className="max-w-7xl mx-auto px-4">
              <div className="flex items-center justify-between h-12 border-b border-slate-700">
                <LogoLockup invert />
                <button
                  onClick={() => setMobileOpen(false)}
                  className="w-11 h-11 flex items-center justify-center hover:text-blue-400 transition-colors"
                  aria-label="Tutup menu"
                >
                  <FontAwesomeIcon icon={faXmark} className="w-5 h-5" />
                </button>
              </div>
              <div className="py-2">
                <a href="/" className={`block px-4 py-3 text-sm ${isActive('/') ? 'text-blue-400' : 'hover:text-blue-400 transition-colors'}`}>Beranda</a>
                {categories.map((cat) => (
                  <a key={cat.id} href={`/kategori/${cat.slug}`} className="block px-6 py-3 text-sm text-slate-400 hover:text-blue-400 transition-colors">{cat.name}</a>
                ))}
                {categories.length === 0 && categoriesState === 'loading' && (
                  <div role="status" aria-live="polite" className="flex items-center gap-2 px-6 py-3 text-sm text-slate-400">
                    <LoadingSpinner className="h-4 w-4 border-slate-600 border-t-blue-400" /> Memuat kategori...
                  </div>
                )}
                {categories.length === 0 && categoriesState === 'error' && (
                  <p className="px-6 py-3 text-sm text-slate-400">Kategori belum dapat ditampilkan.</p>
                )}
                <a href="/katalog" className={`block px-4 py-3 text-sm ${isActive('/katalog') ? 'text-blue-400' : 'hover:text-blue-400 transition-colors'}`}>
                  Katalog
                </a>
                <a href="/merek" className={`block px-4 py-3 text-sm ${isActive('/merek') ? 'text-blue-400' : 'hover:text-blue-400 transition-colors'}`}>Merek</a>
                <a href="/blog" className={`block px-4 py-3 text-sm ${isActive('/blog') ? 'text-blue-400' : 'hover:text-blue-400 transition-colors'}`}>Cara Jualan</a>
                <a href="/cara-jadi-reseller" className={`block px-4 py-3 text-sm ${isActive('/cara-jadi-reseller') ? 'text-blue-400' : 'hover:text-blue-400 transition-colors'}`}>Jadi Reseller</a>
                <a href="/ebook-bikin-toko-online" className={`block px-4 py-3 text-sm ${isActive('/ebook-bikin-toko-online') ? 'text-blue-400' : 'hover:text-blue-400 transition-colors'}`}>Ebook</a>
                <a href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent('Halo Dropify, ada yang ingin saya tanyakan.')}`} {...whatsAppCtaProps('sitewide', { cta_placement: 'nav_mobile' })} target="_blank" rel="noopener noreferrer" className="block px-4 py-3 text-sm hover:text-blue-400 transition-colors">Kontak</a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}

// ─── Social links + brand trademark (shared by main footer and LP footers) ─────


export function SocialLinks({ variant = 'dark' }: { variant?: 'dark' | 'light' }) {
  const buttonClass =
    variant === 'dark'
      ? 'w-11 h-11 bg-slate-800 hover:bg-blue-600 border border-slate-700 hover:border-blue-600 text-slate-400 hover:text-white'
      : 'w-10 h-10 bg-slate-100 hover:bg-blue-600 border border-slate-200 hover:border-blue-600 text-slate-500 hover:text-white'
  return (
    <div className="flex flex-wrap items-center gap-3">
      {SOCIAL_LINKS.map(({ label, href, icon }) => (
        <a
          key={label}
          href={href}
          onClick={() => track('click_social', { social_network: label, link_url: href })}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          className={`${buttonClass} rounded-full flex items-center justify-center transition-colors`}
        >
          <FontAwesomeIcon icon={icon} className="w-4 h-4" />
        </a>
      ))}
    </div>
  )
}

// Registered-trademark line. Rendered on every page/LP footer for brand protection.
export function BrandTrademark({ className = '' }: { className?: string }) {
  return (
    <p className={className}>
      Dropify&reg; adalah merek dagang terdaftar milik PT Dropify Distribusi Indonesia.
    </p>
  )
}

// ─── Footer ───────────────────────────────────────────────────────────────────

export function Footer({ config, categories: categoriesProp = [] }: { config?: StorefrontConfig | null; categories?: StorefrontCategory[] }) {
  const contactPhone = getStoreContactPhone(config)
  const contactEmail = getStoreContactEmail(config)
  const [fetchedCategories, setFetchedCategories] = useState<StorefrontCategory[]>([])

  // Only fetch when the caller didn't supply categories. Depend on the length
  // (a primitive), not the array identity: the default `categoriesProp = []` is a
  // new reference every render, which would otherwise re-run this effect each
  // render and, with the API cache off in dev, loop endlessly.
  useEffect(() => {
    if (categoriesProp.length > 0) return
    let active = true
    listStorefrontCategoriesServer().then((items) => { if (active) setFetchedCategories(items) }).catch(() => { })
    return () => { active = false }
  }, [categoriesProp.length])

  const categories = categoriesProp.length > 0 ? categoriesProp : fetchedCategories

  const landingPageLinks = [
    { label: 'Pusat Grosir', href: '/pusat-grosir' },
    { label: 'Cari Importir', href: '/cari-importir' },
    ...categories.map((category) => ({
      label: `Grosir ${category.name}`,
      href: getCategoryNavigationPath(category.slug),
    })),
    { label: 'Grosir Sarung Tangan', href: '/lp/grosir-sarung-tangan' },
    { label: 'Supplier Sarung Tangan Pabrik', href: '/sarung-tangan-pabrik' },
    { label: 'Sarung Tangan Proyek', href: '/sarung-tangan-proyek' },
    { label: 'Sarung Tangan Paskibra', href: '/sarung-tangan-paskibra' },
    { label: 'Supplier Bra & Lingerie Surabaya', href: '/supplier-bra-lingerie-surabaya' },
    { label: 'Grosir Underwear Surabaya', href: '/grosir-underwear-surabaya' },
    { label: 'Supplier APD Pabrik', href: '/supplier-apd-pabrik' },
    { label: 'Sarung Tangan Nitrile', href: '/sarung-tangan-nitrile' },
    { label: 'Sarung Tangan Latex & Nitrile', href: '/sarung-tangan-latex-nitrile' },
    { label: 'Perlengkapan Haji & Umroh', href: '/perlengkapan-haji-umroh' },
    { label: 'Grosir Perlengkapan Haji', href: '/grosir-perlengkapan-haji-umroh' },
  ]
  const layananKamiLinks = [
    {
      label: 'Cara Pemesanan',
      href: `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent('Halo Dropify, saya ingin mengetahui cara pemesanan produk grosir.')}`,
      external: true,
    },
    {
      label: 'Minimum Order',
      href: '/minimum-order',
      external: false,
    },
    {
      label: 'Ebook Bikin Toko Online',
      href: '/ebook-bikin-toko-online',
      external: false,
    },
    {
      label: 'Bikin Toko Online Sendiri',
      href: '/bikin-toko-online-sendiri',
      external: false,
    },
    {
      label: 'Jadi Reseller',
      href: '/cara-jadi-reseller',
      external: false,
    },
    {
      label: 'Belanja per Merek',
      href: '/merek',
      external: false,
    },
    {
      label: 'Syarat & Ketentuan',
      href: '/syarat-ketentuan',
      external: false,
    },
    {
      label: 'Privasi',
      href: '/privasi',
      external: false,
    },
    {
      label: 'Tentang Kami',
      href: '/tentang-kami',
      external: false,
    },
    {
      label: 'Hubungi Kami',
      href: `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent('Halo Dropify, ada yang ingin saya tanyakan.')}`,
      external: true,
    },
  ]

  return (
    <footer className="bg-slate-900 text-slate-400 pt-14 pb-24 sm:pb-8">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="sr-only">Informasi Footer</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
          {/* Col 1 — Informasi Kontak */}
          <div>
            <h3 className="text-white text-sm font-bold uppercase tracking-wider mb-4">Informasi Kontak</h3>
            <p className="text-sm text-slate-300 leading-relaxed mb-5">
              Dropify melayani pembelian grosir melalui WhatsApp. Kirim nama produk, jumlah, dan kota tujuan untuk meminta quotation.
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-2.5">
                <FontAwesomeIcon icon={faBuilding} className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm">PT Dropify Distribusi Indonesia</span>
              </div>
              <div className="flex items-start gap-2.5">
                <FontAwesomeIcon icon={faLocationDot} className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm">Pusat Grosir, Gubeng, Surabaya, Jawa Timur</span>
              </div>
              <div className="flex items-center gap-2.5">
                <WhatsAppIcon className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <a href={`https://wa.me/${contactPhone.replace(/[^0-9]/g, '')}`} {...whatsAppCtaProps('sitewide', { cta_placement: 'footer_phone' })} target="_blank" rel="noopener noreferrer" className="text-sm hover:text-white transition-colors">{contactPhone}</a>
              </div>
              <div className="flex items-center gap-2.5">
                <FontAwesomeIcon icon={faEnvelope} className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <a href={`mailto:${contactEmail}`} onClick={() => trackContact('email')} className="min-w-0 break-all text-sm hover:text-white transition-colors">{contactEmail}</a>
              </div>
            </div>

            <div className="mt-6 border-t border-slate-800 pt-5">
              <h4 className="text-white text-xs font-bold uppercase tracking-wider mb-3">Metode Pembayaran</h4>
              <div className="flex items-center gap-2.5 flex-wrap">
                <div className="bg-white rounded-lg px-3 py-2 flex items-center justify-center" style={{ height: 42 }}>
                  <img src="/bca.svg" alt="BCA" width="82" height="26" loading="lazy" decoding="async" className="h-6 w-auto object-contain" />
                </div>
                <div className="bg-white rounded-lg px-3 py-2 flex items-center justify-center" style={{ height: 42 }}>
                  <img src="/mandiri.png" alt="Mandiri" width="92" height="26" loading="lazy" decoding="async" className="h-6 w-auto object-contain" />
                </div>
              </div>
            </div>
          </div>

          {/* Col 2 — Layanan Kami */}
          <div>
            <h3 className="text-white text-sm font-bold uppercase tracking-wider mb-4">Layanan Kami</h3>
            <ul className="space-y-0.5">
              {layananKamiLinks.map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href}
                    {...(item.external ? whatsAppCtaProps('sitewide', { cta_placement: 'footer_service' }) : {})}
                    target={item.external ? '_blank' : undefined}
                    rel={item.external ? 'noopener noreferrer' : undefined}
                    className="text-sm hover:text-blue-400 transition-colors flex items-center gap-2 py-1.5"
                  >
                    <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3 text-blue-400" />
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3 — Halaman (landing pages per kategori) */}
          {landingPageLinks.length > 0 && (
            <div>
              <h3 className="text-white text-sm font-bold uppercase tracking-wider mb-4">Halaman</h3>
              <ul className="space-y-0.5">
                {landingPageLinks.map((item) => (
                  <li key={item.href}>
                    <a
                      href={item.href}
                      className="text-sm hover:text-blue-400 transition-colors flex items-center gap-2 py-1.5"
                    >
                      <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3 text-blue-400" />
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Col 4 — Brand landing pages */}
          <div>
            <h3 className="text-white text-sm font-bold uppercase tracking-wider mb-4">Brand</h3>
            <ul className="space-y-0.5">
              {FOOTER_BRAND_LINKS.map((item) => (
                <li key={item.href}>
                  <a
                    href={item.href}
                    className="text-sm hover:text-blue-400 transition-colors flex items-center gap-2 py-1.5"
                  >
                    <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3 text-blue-400" />
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

        </div>

        {/* Footer bottom */}
        <div className="border-t border-slate-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <LogoLockup size="sm" invert />
          <SocialLinks variant="dark" />
        </div>

        {/* Copyright */}
        <div className="mt-5 space-y-1 text-center text-xs text-slate-400">
          <p>&copy; 2026 Dropify - Grosir Distributor Tangan Pertama | Semua hak dilindungi</p>
          <BrandTrademark />
        </div>
      </div>
    </footer>
  )
}
